"use client"

import { useEffect, useRef } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { usePrefersReducedMotion } from "@/components/glass/glass-kit"
import { PulseDot } from "@/components/ui/pulse-dot"
import type { PlatformStats } from "@/lib/platform-stats"

type StatItem = {
  value: number
  label: string
  /** Reveal window as a fraction of the block's rise (0–1). */
  window: [number, number]
}

/**
 * Window custom properties for one revealed element.
 *
 * `--s`/`--e` are in the same space as `--p` (the fallback's 0–1 progress).
 * `--s-pct`/`--e-pct` are the same window expressed against the block's cover
 * range for the CSS timeline; 55 is the share of that range the whole reveal
 * occupies, so everything is lit while the block is still in the upper half
 * of the screen. Same mapping the mission fill uses.
 */
function win([start, end]: [number, number]) {
  return {
    ["--s" as string]: start.toFixed(4),
    ["--e" as string]: end.toFixed(4),
    ["--s-pct" as string]: `${(start * 55).toFixed(2)}%`,
    ["--e-pct" as string]: `${(Math.min(end, 1) * 55).toFixed(2)}%`,
  }
}

/**
 * Live traction strip for the landing page.
 *
 * No pin. The strip sits in normal flow and the stats count up as it rises
 * through the viewport. It used to be a 240svh section with a 100svh sticky
 * child, which on a phone read as the page getting stuck — the same
 * complaint the mission paragraph had before #327 unpinned it.
 *
 * It also used to take scroll progress into React state, so every frame of a
 * scroll re-rendered this whole tree. Now nothing re-renders on scroll:
 * browsers with scroll-driven animations (Safari 26, Chrome) run the reveal
 * entirely on the compositor from the block's own view timeline, and the
 * rest get one `--p` write per frame. Both paths land on the same custom
 * property, `--tracker-f`, and CSS derives the numbers and the fade from it
 * (.tracker-* in globals.css).
 */
export function LandingTractionTracker({ stats }: { stats: PlatformStats }) {
  const t = useTranslations("marketing.landing.tracker")
  const reduced = usePrefersReducedMotion()
  const blockRef = useRef<HTMLDivElement>(null)

  const items: StatItem[] = [
    { value: stats.barberCount, label: t("barbers"), window: [0.1, 0.46] },
    { value: stats.shopCount, label: t("barbershops"), window: [0.26, 0.62] },
    { value: stats.cityCount, label: t("cities"), window: [0.42, 0.78] },
  ]

  const leadWindow: [number, number] = [0.04, 0.2]
  const linkWindow: [number, number] = [0.7, 0.92]

  // Fallback only — browsers with scroll-driven animations never run this.
  useEffect(() => {
    if (reduced) return
    if (typeof CSS !== "undefined" && CSS.supports("animation-timeline: view()")) return
    const el = blockRef.current
    if (!el) return
    let raf = 0
    const tick = () => {
      raf = 0
      const r = el.getBoundingClientRect()
      const vh = window.innerHeight
      const p = Math.min(1, Math.max(0, (vh - r.top) / (0.55 * (vh + r.height))))
      el.style.setProperty("--p", p.toFixed(4))
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick)
    }
    tick()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [reduced])

  const lineClass =
    "font-extralight tracking-tighter tabular-nums text-[clamp(2.4rem,7.8vw,4.25rem)] leading-[1.02]"

  const labelClass = "ml-[0.22em] text-[0.42em] font-light tracking-[-0.03em]"

  if (reduced) {
    return (
      <section className="relative overflow-x-clip px-5 py-20 sm:px-6 sm:py-28" aria-label={t("ariaLabel")}>
        <div className="mx-auto w-full max-w-2xl text-center">
          <p
            className="inline-flex items-center justify-center gap-2 text-[11px] font-light uppercase tracking-[0.22em]"
            style={{ color: "var(--pg-faint)" }}
          >
            <PulseDot />
            {t("live")}
          </p>
          <p
            className="mt-8 text-[clamp(1rem,2.4vw,1.25rem)] font-light tracking-[-0.02em]"
            style={{ color: "var(--pg-muted)" }}
          >
            {t("lead")}
          </p>
          <div className="mt-3 flex flex-col items-center gap-1 sm:mt-4 sm:gap-1.5">
            {items.map((item) => (
              <p key={item.label} className={lineClass} style={{ color: "var(--pg-fg)" }}>
                {item.value.toLocaleString()}
                <span className={labelClass} style={{ color: "var(--pg-muted)" }}>
                  {item.label}
                </span>
              </p>
            ))}
          </div>
          <p className="mt-10 sm:mt-12">
            <Link
              href="/traction"
              className="text-[13px] font-light tracking-[-0.01em] transition-opacity duration-300 hover:opacity-70"
              style={{ color: "var(--pg-faint)" }}
            >
              {t("link")} →
            </Link>
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="relative overflow-x-clip px-5 py-24 sm:px-6 sm:py-32" aria-label={t("ariaLabel")}>
      <div
        ref={blockRef}
        className="tracker-block mx-auto w-full max-w-2xl text-center"
        style={{ ["--p" as string]: 0 }}
      >
        <p
          className="tracker-item tracker-fade inline-flex items-center justify-center gap-2 text-[11px] font-light uppercase tracking-[0.22em]"
          style={{ color: "var(--pg-faint)", ...win(leadWindow) }}
        >
          <PulseDot />
          {t("live")}
        </p>

        <p
          className="tracker-item tracker-fade mt-8 text-[clamp(1rem,2.4vw,1.25rem)] font-light tracking-[-0.02em]"
          style={{ color: "var(--pg-muted)", ...win(leadWindow) }}
        >
          {t("lead")}
        </p>

        <div className="mt-3 flex flex-col items-center gap-1 sm:mt-4 sm:gap-1.5">
          {items.map((item) => (
            <p
              key={item.label}
              className={`tracker-item tracker-stat ${lineClass}`}
              style={{
                color: "var(--pg-fg)",
                ["--target" as string]: Math.max(0, Math.round(item.value)),
                ...win(item.window),
              }}
            >
              {/* The digits are a CSS counter off --tracker-f, so counting up
                  costs no render. Screen readers get the settled value from
                  the sibling instead — generated content is not dependable
                  there, and a number ticking in the accessibility tree would
                  be noise either way. */}
              <span aria-hidden className="tracker-stat-num" />
              <span className="sr-only">
                {item.value.toLocaleString()} {item.label}
              </span>
              <span aria-hidden className={labelClass} style={{ color: "var(--pg-muted)" }}>
                {item.label}
              </span>
            </p>
          ))}
        </div>

        <p className="tracker-item tracker-fade mt-10 sm:mt-12" style={win(linkWindow)}>
          <Link
            href="/traction"
            className="text-[13px] font-light tracking-[-0.01em] transition-opacity duration-300 hover:opacity-70"
            style={{ color: "var(--pg-faint)" }}
          >
            {t("link")} →
          </Link>
        </p>
      </div>
    </section>
  )
}
